import type { GridProps } from '@chakra-ui/react'
import { Button, Grid, Stack } from '@chakra-ui/react'
import type {
  DetailedUser,
  EditUser,
  UserProperties
} from 'BrokerTsLib/src/dtos'
import {
  BadRequestError
} from 'BrokerTsLib/src/dtos'
import { FormProvider, useForm, useFormContext } from 'BrokerTsLib/src/rhf'
import { setModelStateErrors } from 'BrokerTsLib/src/Utils/formModelState'
import type { Ref } from 'react'
import React, { forwardRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { CustomWaypoint } from '@/components'
import {
  UserAccessFormSection,
  UserInfoFormSection,
  UserMotivationFormSection,
  UserPersonalNumbersFormSection,
} from '@/components/User'
import { usePartner } from '@/context/PartnerContext'
import { useSidebar } from '@/context/SidebarContext'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { ArrowLeft } from '../../icons'

export const UserForm = ({
  user,
  properties,
  onSubmit,
  isCreate = false,
}: {
  user: DetailedUser
  properties: UserProperties
  onSubmit: (
    values: EditUser,
  ) => Promise<void | BadRequestError<EditUser> | Error>
  isCreate?: boolean
}) => {
  const { currentPartner } = usePartner()
  const methods = useForm<EditUser>({
    defaultValues: {
      ...user,
      personalNumbers: user.personalNumbers?.sort((a, b) =>
        a.insurerName.localeCompare(b.insurerName)
      ) ?? [],
      properties: { ...properties },
    },
    mode: 'onBlur',
  })
  const { handleSubmit, setError, reset } = methods

  useEffect(() => {
    reset({ ...user, properties: { ...properties } })
  }, [user, properties])

  const { pageSections, setPageSections } = useSidebar()

  const submitHandler = (values: EditUser) =>
    onSubmit(values).then((response) => {
      if (response instanceof BadRequestError)
        setModelStateErrors<EditUser>(setError, response)
    })

  useEffect(() => {
    const array: Section[] = []
    let i = 1

    array.push({
      id: 'access',
      sectionTitle: 'Nastavení přístupu',
      index: i++,
      children: (
        <CustomWaypoint sectionId="access">
          <UserAccessFormSection
            user={user}
            isCreate={isCreate}
            id="access"
            number={i - 1}
          />
        </CustomWaypoint>
      ),
    })

    array.push({
      id: 'userInfo',
      sectionTitle: 'Informace o uživateli',
      index: i++,
      children: (
        <CustomWaypoint sectionId="userInfo">
          <UserInfoFormSection id="userInfo" number={i - 1} />
        </CustomWaypoint>
      ),
    })

    array.push({
      id: 'personalNumbers',
      sectionTitle: 'Osobní čísla',
      index: i++,
      children: (
        <CustomWaypoint sectionId="personalNumbers">
          <UserPersonalNumbersFormSection
            id="personalNumbers"
            number={i - 1}
            user={user}
          />
        </CustomWaypoint>
      ),
    })

    if (currentPartner?.partner === 'Jap') {
      array.push({
        id: 'userMotivation',
        sectionTitle: 'Motivace',
        index: i++,
        children: (
          <CustomWaypoint sectionId="userMotivation">
            <UserMotivationFormSection id="userMotivation" number={i - 1} />
          </CustomWaypoint>
        ),
      })
    }

    array.push({
      id: 'footer',
      title: 'Konec',
      sectionTitle: 'Konec',
      isHidden: true,
      index: i++,
      children: (
        <CustomWaypoint sectionId="footer">
          <UserEditFormFooter
            id="footer"
            isCreate={isCreate}
            user={user}
            onSubmit={submitHandler}
          />
        </CustomWaypoint>
      ),
    })

    setPageSections(array)

    return () => setPageSections([])
  }, [user, isCreate, setPageSections])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Stack gap={8}>
          {pageSections.map((x) => (
            <React.Fragment key={x.id}>{x.children}</React.Fragment>
          ))}
        </Stack>
      </form>
    </FormProvider>
  )
}

const UserEditFormFooter = forwardRef(function UserEditFormFooter(
  {
    user,
    isCreate,
    onSubmit,
    ...rest
  }: {
    user: DetailedUser
    isCreate?: boolean
    onSubmit: (values: EditUser) => Promise<void>
  } & Omit<GridProps, 'onSubmit'>,
  ref: Ref<HTMLDivElement>,
) {
  const navigate = useNavigate()
  const {
    formState: { isSubmitting },
    handleSubmit,
  } = useFormContext<EditUser>()
  return (
    <Grid
      ref={ref}
      sx={{
        width: '100%',
        gridTemplateColumns: 'auto auto',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      {...rest}
    >
      <Button
        variant="ghost"
        isLoading={isSubmitting}
        type="button"
        onClick={() => user.userId ? navigate(`/user/${user.userId}`) : navigate(-1)}
      >
        <ArrowLeft color="secondary.500" />
        Zpět
      </Button>
      <Button
        colorScheme="primary"
        isLoading={isSubmitting}
        onClick={() => handleSubmit(onSubmit)()}
        px={6}
      >
        {isCreate ? 'Vytvořit' : 'Uložit'}
      </Button>
    </Grid>
  )
})
